// frontend/src/app/admin/observability/prom.ts
import { apiJson } from "@/lib/api";

export type PromPoint = { t: number; v: number };
export type PromSeries = { metric: Record<string, string>; points: PromPoint[] };

type PromResponse = {
  status: "success" | "error";
  data?: {
    resultType?: "matrix" | "vector" | "scalar" | "string";
    result?: any[];
  };
  errorType?: string;
  error?: string;
  warnings?: string[];
};

function num(x: any): number {
  const n = typeof x === "number" ? x : parseFloat(String(x));
  return Number.isFinite(n) ? n : 0;
}

function parseRangeResult(result: any[]): PromSeries[] {
  // result item for range: { metric: {...}, values: [[unixSec, "val"], ...] }
  return (result || []).map((r: any) => {
    const metric = (r?.metric && typeof r.metric === "object") ? r.metric : {};
    const values = Array.isArray(r?.values) ? r.values : [];
    const points: PromPoint[] = values.map((pair: any) => {
      const tsSec = Array.isArray(pair) ? pair[0] : 0;
      const val = Array.isArray(pair) ? pair[1] : 0;
      return { t: Math.round(num(tsSec) * 1000), v: num(val) };
    });
    return { metric, points };
  });
}

function parseInstantResult(result: any[]): PromSeries[] {
  // result item for instant: { metric: {...}, value: [unixSec, "val"] }
  return (result || []).map((r: any) => {
    const metric = (r?.metric && typeof r.metric === "object") ? r.metric : {};
    const value = Array.isArray(r?.value) ? r.value : [0, "0"];
    const tsSec = value[0];
    const val = value[1];
    return { metric, points: [{ t: Math.round(num(tsSec) * 1000), v: num(val) }] };
  });
}

/**
 * Convert a Prometheus /api/v1/query or /api/v1/query_range response into
 * a stable series array for our chart components.
 */
export function toSeries(resp: PromResponse): PromSeries[] {
  const rt = resp?.data?.resultType;
  const r = Array.isArray(resp?.data?.result) ? resp!.data!.result! : [];

  if (rt === "matrix") return parseRangeResult(r);
  if (rt === "vector") return parseInstantResult(r);

  // scalar/string -> represent as one point “now”
  if (rt === "scalar" && Array.isArray(r) && r.length >= 2) {
    const tsSec = r[0];
    const val = r[1];
    return [{ metric: {}, points: [{ t: Math.round(num(tsSec) * 1000), v: num(val) }] }];
  }

  return [];
}

/**
 * Helper: produce a time window [start,end] in ms for query_range
 */
export function rangeWindowMs(hours: number): { start: number; end: number } {
  const end = Date.now();
  const start = end - Math.max(1, hours) * 60 * 60 * 1000;
  return { start, end };
}

export async function promQuery(query: string, tenantUuid: string): Promise<PromResponse> {
  const qs = new URLSearchParams({ tenant_id: tenantUuid, query });
  // Backend proxy route you planned: /v1/admin/observability/prom/query
  return apiJson<PromResponse>(`/v1/admin/observability/prom/query?${qs.toString()}`);
}

export async function promQueryRange(
  query: string,
  tenantUuid: string,
  args: { start: number; end: number; step: string }
): Promise<PromResponse> {
  // Prom expects seconds; we keep ms in UI for convenience.
  const startSec = Math.floor(args.start / 1000);
  const endSec = Math.floor(args.end / 1000);

  const qs = new URLSearchParams({
    tenant_id: tenantUuid,
    query,
    start: String(startSec),
    end: String(endSec),
    step: args.step,
  });

  // Backend proxy route you planned: /v1/admin/observability/prom/query_range
  return apiJson<PromResponse>(`/v1/admin/observability/prom/query_range?${qs.toString()}`);
}